import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormFeedback, Form, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import type { RootState } from "store";
import MobileInput from "../../Components/Common/MobileInput";
import appConfig from "../../config";
import { addNewLeadThunk } from "../../slices/Lead/thunk";
import { resetLeadState } from "../../slices/Lead/reducer";
import { showSuccessToast, showErrorToast } from "../../helpers/toast_helper";

interface LeadModalProps {
  isOpen: boolean;
  toggle: () => void;
  serviceName?: string;
  productName?: string;
  onSubmit?: (values: { name: string; email: string; mobileNumber: string; dialCode: string; countryCode: string }) => void;
}

const LeadModal: React.FC<LeadModalProps> = ({ isOpen, toggle, serviceName = "Insurance", productName = "", onSubmit }) => {
  const dispatch = useDispatch<any>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const leadStateSelector = createSelector(
    (state: RootState) => (state as any).lead,
    (lead) => ({
      loading: lead?.loading ?? false,
      error: lead?.error ?? null,
      success: lead?.success ?? false,
      successMessage: lead?.successMessage ?? null,
    })
  );

  const { loading, error, success, successMessage } = useSelector(leadStateSelector);
  const isLoading = loading || isSubmitting;

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      email: '',
      mobileNumber: '',
      dialCode: appConfig.country.defaultDialCode,
      countryCode: appConfig.country.defaultCountryCode,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .min(2, "Name must be at least 2 characters"),
      email: Yup.string()
        .required("Email is required")
        .email("Please enter a valid email address"),
      mobileNumber: Yup.string()
        .required("Mobile number is required")
        .matches(/^[0-9]{6,10}$/, "Mobile number must be between 6 and 10 digits"),
      dialCode: Yup.string().required("Dial code is required"),
      countryCode: Yup.string().required("Country code is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          service_name: serviceName,
          product_name: productName || "Insurance Product",
          contact_name: values.name,
          email_id: values.email,
          country_code: values.countryCode,
          dial_code: values.dialCode,
          phone_no: values.mobileNumber,
        };

        console.log("=== LEAD FORM SUBMISSION ===");
        console.log("Payload being sent:", JSON.stringify(payload, null, 2));
        console.log("======================================");

        const result = await dispatch(addNewLeadThunk(payload));
        
        console.log("=== LEAD FORM RESPONSE ===");
        console.log("Result:", result);
        console.log("======================================");
        
        if (result?.success) {
          // Show success toast with message from API response
          const successMessage = result.message || "Lead has been saved successfully";
          showSuccessToast(successMessage, "lead-success");
          // Reset form and close modal
          validation.resetForm();
          dispatch(resetLeadState());
          toggle();
          // Call onSubmit callback if provided
          if (onSubmit) {
            await onSubmit(values);
          }
        } else {
          // Show error toast with message from API response
          const errorMessage = result?.message || "Failed to save lead. Please try again.";
          showErrorToast(errorMessage, "lead-error");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Keep useEffect for error as fallback (in case error state is updated from elsewhere)
  useEffect(() => {
    if (error) {
      // Show toast with exact API response message (error case)
      showErrorToast(error, "lead-error");
      // Clear error state after showing toast
      dispatch(resetLeadState());
    }
  }, [error, dispatch]);

  const handleCancel = () => {
    validation.resetForm();
    dispatch(resetLeadState());
    toggle();
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
      dispatch(resetLeadState());
    }
  }, [isOpen, dispatch, validation]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="lead-modal">
      <ModalHeader toggle={toggle} className="lead-modal-header">
        Details
      </ModalHeader>
      <ModalBody className="lead-modal-body">
        <p className="lead-modal-description">
          Fill information to get the details about program
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <div className="lead-form-group">
            <Label htmlFor="name" className="lead-form-label">
              NAME
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              className="lead-form-input"
              placeholder="Type here.."
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name}
              invalid={validation.touched.name && !!validation.errors.name}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            )}
          </div>

          <div className="lead-form-group">
            <Label htmlFor="email" className="lead-form-label">
              EMAIL
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              className="lead-form-input"
              placeholder="Type here.."
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.email}
              invalid={validation.touched.email && !!validation.errors.email}
            />
            {validation.touched.email && validation.errors.email && (
              <FormFeedback type="invalid">
                {validation.errors.email}
              </FormFeedback>
            )}
          </div>

          <div className="lead-form-group">
            <Label htmlFor="mobileNumber" className="lead-form-label">
              MOBILE NUMBER
            </Label>
            <MobileInput
              name="mobileNumber"
              value={validation.values.mobileNumber}
              onChange={(e) => {
                // Allow 6 to 10 digits - remove non-digits and limit to 10
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                validation.setFieldValue('mobileNumber', value);
              }}
              onBlur={validation.handleBlur}
              dialcode={validation.values.dialCode}
              onDialCodeChange={(dialcode, countrycode) => {
                validation.setFieldValue('dialCode', dialcode);
                validation.setFieldValue('countryCode', countrycode);
              }}
              error={validation.errors.mobileNumber}
              touched={validation.touched.mobileNumber}
              placeholder="Type here.."
            />
          </div>
        </Form>
      </ModalBody>
      <ModalFooter className="lead-modal-footer">
        <Button
          color="secondary"
          onClick={handleCancel}
          className="lead-modal-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          color="primary"
          onClick={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
          disabled={isLoading}
          className="lead-modal-send-btn"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Sending...
            </>
          ) : (
            "Send"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default LeadModal;

